import React from 'react';
import { motion } from 'framer-motion';

interface Integration {
  name: string;
  description: string;
  logo: string;
  logoType: 'svg' | 'png';
}

const INTEGRATIONS: Integration[] = [
  {
    name: 'Flowww',
    description: 'Centros de estética, belleza y bienestar conectados a atención y campañas',
    logo: '/logos/flowww.svg',
    logoType: 'svg',
  },
  {
    name: 'TIMP',
    description: 'Agenda y gestión para fisioterapia, pilates y servicios recurrentes',
    logo: '/logos/timp.svg',
    logoType: 'svg',
  },
  {
    name: 'Booksy',
    description: 'Reservas de peluquerías, barberías y estética conectadas a WhatsApp',
    logo: '/logos/booksy.svg',
    logoType: 'svg',
  },
  {
    name: 'Treatwell',
    description: 'Reservas para centros de belleza sin dobles huecos ni seguimiento manual',
    logo: '/logos/treatwell.svg',
    logoType: 'svg',
  },
  {
    name: 'Google Calendar',
    description: 'Sincronización bidireccional de agenda, disponibilidad y recordatorios',
    logo: '/logos/googlecalendar.svg',
    logoType: 'svg',
  },
  {
    name: 'WhatsApp',
    description: 'Canal principal de atención, reservas, seguimiento y campañas aprobadas',
    logo: '/logos/whatsapp.svg',
    logoType: 'svg',
  },
  {
    name: 'Instagram',
    description: 'Publicación automática o asistida y respuesta a DMs cuando aplica',
    logo: '/logos/instagram.svg',
    logoType: 'svg',
  },
  {
    name: 'Google Business',
    description: 'Publicaciones, reseñas, ofertas y visibilidad local con enlaces medibles',
    logo: '/logos/googlebusiness.svg',
    logoType: 'svg',
  },
  {
    name: 'Gmail',
    description: 'Email, notificaciones internas y seguimiento comercial conectado',
    logo: '/logos/gmail.svg',
    logoType: 'svg',
  },
  {
    name: 'Google Sheets',
    description: 'CRM ligero, informes, listas de leads y operaciones personalizadas',
    logo: '/logos/googlesheets.svg',
    logoType: 'svg',
  },
  {
    name: 'Stripe',
    description: 'Pagos, suscripciones y cobros integrados cuando el flujo lo requiere',
    logo: '/logos/stripe.svg',
    logoType: 'svg',
  },
  {
    name: 'Google Analytics',
    description: 'Métricas de conversión y rendimiento de campañas',
    logo: '/logos/googleanalytics.svg',
    logoType: 'svg',
  },
];

export const IntegrationsSection: React.FC = () => {
  return (
    <section
      id="integraciones"
      className="py-16 relative overflow-hidden"
      style={{ background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)' }}
    >
      <div className="container mx-auto px-4 max-w-7xl relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center max-w-2xl mx-auto mb-12"
        >
          <span
            className="inline-flex items-center gap-2 py-1 px-4 rounded-full text-xs font-bold uppercase tracking-widest mb-4"
            style={{
              background: 'rgba(60,179,113,0.1)',
              border: '1px solid rgba(60,179,113,0.3)',
              color: '#2d8653',
            }}
          >
            Integraciones
          </span>
          <h2 className="text-3xl md:text-4xl font-black text-slate-900 mb-4 tracking-tight">
            Nos integramos con el software que ya mueve tu negocio
          </h2>
          <p className="text-slate-500 font-light leading-relaxed">
            No te pedimos cambiar de agenda, CRM o sistema de reservas. Ancora se conecta alrededor de tu operativa para añadir IA donde más retorno genera: atención, seguimiento, ventas y marketing.
          </p>
        </motion.div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {INTEGRATIONS.map((int, i) => (
            <motion.div
              key={int.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.04 }}
              className="group rounded-2xl p-4 text-center transition-all duration-300 hover:-translate-y-1 cursor-default"
              style={{
                background: '#ffffff',
                border: '1px solid rgba(0,0,0,0.06)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
              }}
            >
              <div className="h-12 flex items-center justify-center mb-3">
                <img
                  src={int.logo}
                  alt={int.name}
                  className="max-h-10 max-w-20 object-contain"
                  loading="lazy"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              </div>
              <h4 className="text-sm font-bold text-slate-900 mb-1">{int.name}</h4>
              <p className="text-xs text-slate-400 leading-snug">{int.description}</p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="mt-10 grid gap-4 md:grid-cols-5"
        >
          {[
            ['Flowww + WhatsApp', '/blog/integracion-flowww-whatsapp-clinicas-estetica.html'],
            ['Booksy + WhatsApp', '/blog/integracion-booksy-whatsapp-reservas.html'],
            ['Treatwell + WhatsApp', '/blog/integracion-treatwell-whatsapp-reservas.html'],
            ['TIMP + WhatsApp', '/blog/integracion-timp-whatsapp-reservas.html'],
            ['VAPI + WhatsApp', '/blog/recepcionista-virtual-vapi-whatsapp.html'],
          ].map(([label, href]) => (
            <a
              key={href}
              href={href}
              className="rounded-2xl border border-emerald-100 bg-white p-4 text-center text-sm font-bold text-slate-800 shadow-sm transition hover:-translate-y-1 hover:border-emerald-300 hover:text-emerald-700 hover:shadow-lg"
            >
              {label}
            </a>
          ))}
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.35 }}
          className="text-center text-sm text-slate-400 mt-6"
        >
          Seguís usando vuestro software. <span className="text-emerald-600 font-medium">Ancora añade la capa inteligente.</span> ¿Usas otro sistema? Lo conectamos contigo.
        </motion.p>
      </div>
    </section>
  );
};